// UpdateReturnLines.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@store/hooks';
import Loading from '@components/Shared/Loader/Loading';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import { Card } from '@components/card/card';

// ✅ نفس آلية POLines (تفاصيل الصنف/الوحدة/الباركود)
import { fetchItemsWithUomLookup, type ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';

interface LinesProps {
  formik: FormikProps<any>;
}

type ItemType = 'InventoryItem' | 'ExpenseItem' | 'FixedAssetItem' | 'ServiceItem' | 'UndefinedItem';
const DEFAULT_ITEM_TYPE: ItemType = 'InventoryItem';

const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const round2 = (v: any) => {
  const n = Number(v || 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

const makeKey = (itemId: any, uom: any) => `${String(itemId || '')}|${String(uom || '')}`;

const UpdateReturnLines: React.FC<LinesProps> = ({ formik }) => {
  const { i18n, t } = useTranslation();
  const { PurchaseReturnById: data } = useAppSelector(s => s.ReturnsPurchaseOrder);

  const values = formik.values || {};
  const inventoryId: string | null = values?.inventoryId ?? data?.inventoryId ?? null;

  const lines = (values.transactionsLinesList || []) as any[];

  // console.log(lines);

  // ======================================================
  // ✅ 1) حساب المتاح الحقيقي: (فاتورة الأصل) - (المردودات السابقة)
  //    على مستوى itemId|uom (الأدق)
  // ======================================================
  function calculateAvailableQtyReturn(invoice: any, returns: any) {
    const available = new Map<string, number>();

    // كميات الفاتورة الأصل
    invoice?.transactionsLinesList?.forEach((line: any) => {
      const key = makeKey(line?.itemId, line?.uom);
      available.set(key, (available.get(key) || 0) + Number(line?.qty || 0));
    });

    // طرح الكميات المسترجعة سابقاً
    (returns || [])?.forEach((retDoc: any) => {
      retDoc?.transactionsLinesList?.forEach((line: any) => {
        const key = makeKey(line?.itemId, line?.uom);
        available.set(key, (available.get(key) || 0) - Number(line?.qty || 0));
      });
    });

    return available;
  }

  const baseAvailableMap = useMemo(() => {
    return calculateAvailableQtyReturn(data, data?.purTransactionsDtoList);
  }, [data]);

  // ======================================================
  // ✅ 2) Snapshot لكمية السطر عند فتح الشاشة (ثابت)
  //    عشان maxAvail ما يزيد مع تعديل qty
  // ======================================================
  const initialQtyRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const m = initialQtyRef.current;

    for (const l of formik.values.transactionsLinesList || []) {
      if (!l?.itemId || !l?.uom) continue;

      const key = makeKey(l.itemId, l.uom);

      // ⚠️ افتراض شائع: لا يوجد تكرار itemId|uom ضمن البنود
      // إن كان يوجد تكرار فعلي عندك، قلّي نعدل المفتاح ليكون per-line.
      if (!m.has(key)) {
        m.set(key, Number(l.qty || 0));
      }
    }
  }, [formik.values.transactionsLinesList]);

  // ======================================================
  // ✅ 3) availableQtyReturn ثابت = baseAvail + initialQtySnapshot
  // ======================================================
  const linesWithAvailable = useMemo(() => {
    return lines.map(l => {
      const key = makeKey(l?.itemId, l?.uom);
      const baseAvail = Number(baseAvailableMap.get(key) || 0);
      const initialQty = Number(initialQtyRef.current.get(key) ?? 0);

      return {
        ...l,
        availableQtyReturn: baseAvail + initialQty // ✅ ثابت
      };
    });
  }, [lines, baseAvailableMap]);

  // ======================================================
  // ✅ 4) DetailsMap مثل POLines (اسم الصنف/الوحدة/الباركود)
  // ======================================================
  const [detailsMap, setDetailsMap] = useState<Map<string, ItemWithUomRow>>(new Map());
  const [detailsLoading, setDetailsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const getInfo = (line: any) => {
    if (!line?.itemId || !line?.uom) return null;
    return detailsMap.get(`${line.itemId}|${line.uom}`) || null;
  };

  const getItemNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const name = isEn ? info?.name || info?.name2 : info?.name2 || info?.name;
    return name || line?.description || (line?.itemId ? `ID: ${line.itemId}` : '');
  };

  const getUomNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const uomName = isEn
      ? info?.unitOfMeasureName || info?.unitOfMeasureName2
      : info?.unitOfMeasureName2 || info?.unitOfMeasureName;
    return uomName || '';
  };

  // ✅ المفتاح لتفادي إعادة fetch عند تغيير qty/description
  const linesKey = useMemo(() => {
    return (lines || [])
      .map((l: any) => `${l?.itemType || DEFAULT_ITEM_TYPE}:${l?.itemId || ''}:${l?.uom || ''}`)
      .join('|');
  }, [lines]);

  useEffect(() => {
    const run = async () => {
      if (!inventoryId) return;

      const valid = (lines || []).filter((l: any) => l?.itemId && l?.uom);
      if (!valid.length) return;

      const missingByType = new Map<string, Set<string>>();
      for (const l of valid) {
        const key = `${l.itemId}|${l.uom}`;
        if (detailsMap.has(key)) continue;

        const type = String(l.itemType || DEFAULT_ITEM_TYPE);
        if (!missingByType.has(type)) missingByType.set(type, new Set());
        missingByType.get(type)!.add(String(l.itemId));
      }

      if (missingByType.size === 0) return;

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setDetailsLoading(true);
      try {
        const merged = new Map(detailsMap);

        for (const [type, idsSet] of missingByType.entries()) {
          const itemIds = Array.from(idsSet);

          for (const idsChunk of chunk(itemIds, 200)) {
            const resp = await fetchItemsWithUomLookup(
              { InventoryId: inventoryId, Scope: 1, WithLastPrice: true },
              { pageIndex: 1, pageSize: 3000, itemType: type, itemIds: idsChunk } as any,
              abortRef.current.signal
            );

            for (const r of resp.data?.data || []) {
              merged.set(`${r.itemId}|${r.unitOfMeasure}`, r);
            }
          }
        }

        setDetailsMap(merged);
      } catch (e: any) {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') console.error(e);
      } finally {
        setDetailsLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, linesKey]);

  // ======================================================
  // ✅ 5) Recalc totals (مثل الأصلي)
  // ======================================================
  const recalcLine = (line: any) => {
    const qty = Number(line?.qty || 0);
    const price = Number(line?.price || 0);

    const total = round2(qty * price);
    const vatRate = clamp(Number(line?.vatRate || 0), 0, 100);
    const vatAmount = round2((total * vatRate) / 100);

    line.total = total;
    line.vatRate = vatRate;
    line.vatAmount = vatAmount;
    line.totalWithVat = round2(total + vatAmount);
  };

  // ======================================================
  // ✅ 6) تعديل qty فقط وبحد ثابت availableQtyReturn
  // ======================================================
  const handleQtyChange = (index: number, raw: any) => {
    const updated = [...(formik.values.transactionsLinesList || [])];
    if (!updated[index]) return;

    const maxAvail = Number(linesWithAvailable[index]?.availableQtyReturn ?? 0);
    const nextQty = clamp(Number(raw || 0), 0, maxAvail);

    const line = { ...updated[index], qty: nextQty };
    recalcLine(line);

    updated[index] = line;
    formik.setFieldValue('transactionsLinesList', updated);
  };

  const handleDescChange = (index: number, value: string) => {
    const updated = [...(formik.values.transactionsLinesList || [])];
    if (!updated[index]) return;

    updated[index] = { ...updated[index], description: value };
    formik.setFieldValue('transactionsLinesList', updated);
  };

  // ✅ إجمالي
  // const totalSum = useMemo(() => {
  //   const arr = (formik.values.transactionsLinesList || []) as any[];
  //   const total = arr.reduce((s, l) => s + Number(l?.total || 0), 0);
  //   const sumVatAmount = arr.reduce((s, l) => s + Number(l?.vatAmount || 0), 0);
  //   const sum = total + sumVatAmount
  //   return formatNumber(sum);
  // }, [formik.values.transactionsLinesList]);

  const totalSum = useMemo(() => {
    const lines = formik.values.transactionsLinesList || [];

    if (!Array.isArray(lines) || lines.length === 0) {
      return formatNumber(0);
    }

    const sum = lines.reduce((acc, line) => {
      if (!line || typeof line !== 'object') return acc;

      const total = Number(line.total) || 0;
      const vat = Number(line.vatAmount) || 0;

      return acc + (total + vat);
    }, 0);

    return formatNumber(isNaN(sum) ? 0 : sum);
  }, [formik.values.transactionsLinesList]);

  return (
    <>
      <Loading loading={false} error={null}>
        <div className='mb-3'>
          <Card>
            <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
              <div className='d-flex flex-wrap gap-4 justify-content-between align-items-center'>
                <div className='d-flex gap-2 align-items-center'>
                  <span className='fw-bold'>{t('Total')}:</span>
                  <span className='text-theme fs-5 fw-bold'>{totalSum}</span>
                </div>

                {detailsLoading && (
                  <div className='d-flex align-items-center'>
                    <span className='spinner-border spinner-border-sm text-theme me-2'></span>
                    <small className='text-muted'>{t('جاري تحميل التفاصيل...')}</small>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {linesWithAvailable?.length > 0 && (
            <div className='mt-2'>
              <div
                className={`table-responsive rounded-2 text-center ${
                  formik.errors.transactionsLinesList ? 'border border-2 border-danger' : ''
                }`}
              >
                <table className='table table-borderless table-hover mb-0' style={{ minWidth: 1100 }}>
                  <thead className='bg-inverse bg-opacity-30'>
                    <tr>
                      <th className='align-middle' style={{ width: '4%' }}>
                        #
                      </th>

                      {/* ✅ نفس شكل “الصنف/الباركود” لكن بدون مسح/scan */}
                      <th className='align-middle' style={{ width: '45%' }}>
                        {t('الصنف / الباركود')}
                      </th>

                      <th className='align-middle' style={{ width: '12%' }}>
                        {t('الوحدة')}
                      </th>

                      <th className='align-middle' style={{ width: '12%' }}>
                        {t('الكمية المسترجعة')}
                      </th>

                      <th className='align-middle' style={{ width: '10%' }}>
                        {t('السعر')}
                      </th>
                      <th className='align-middle' style={{ width: '10%' }}>
                        {t('الضريبة')}
                      </th>

                      <th className='align-middle' style={{ width: '10%' }}>
                        {t('Total')}
                      </th>

                      {/* <th className='align-middle' style={{ width: '11%' }}>
                        {t('الوصف')}
                      </th> */}
                    </tr>
                  </thead>

                  <tbody className='fw-bold'>
                    {linesWithAvailable
                      .slice()
                      .reverse()
                      .map((line: any, revIndex: number) => {
                        const index = linesWithAvailable.length - 1 - revIndex;

                        const info = getInfo(line);
                        const itemName = line.itemId ? getItemNameForLine(line) : '';
                        const uomName = line.itemId ? getUomNameForLine(line) : '';
                        const barcode = info?.barcode || '';

                        const barcodeDisplay = line.itemId
                          ? barcode ||
                            (detailsLoading ? (i18n.language === 'en' ? 'Loading...' : '...جاري التحميل') : '-')
                          : '-';

                        const uomDisplay =
                          uomName ||
                          (detailsLoading
                            ? i18n.language === 'en'
                              ? 'Loading...'
                              : '...جاري التحميل'
                            : line.uom
                            ? '-'
                            : '-');

                        const maxAvail = Number(line.availableQtyReturn ?? 0);

                        return (
                          <tr key={index}>
                            <td className='align-middle bg-inverse bg-opacity-30'>
                              {linesWithAvailable.length - revIndex}
                            </td>

                            {/* ✅ نفس UI في POLines (border + details) بدون أزرار */}
                            <td className='align-middle text-start'>
                              <div className='p-2 border border-theme bg-body'>
                                <div className='d-flex align-items-start gap-2'>
                                  <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                    <span className='fw-bold text-truncate d-block'>
                                      {itemName ||
                                        (detailsLoading
                                          ? i18n.language === 'en'
                                            ? 'Loading...'
                                            : '...جاري التحميل'
                                          : `ID: ${line.itemId}`)}
                                    </span>

                                    <small className='text-muted d-block'>
                                      {t('الوحدة')}: <span className='fw-bold text-theme'>{uomDisplay}</span>
                                      <span className='mx-2'>•</span>
                                      {t('باركود')}: <span className='fw-bold'>{barcodeDisplay}</span>
                                      <span className='mx-2'>•</span>
                                      {t('المتاح')}: <span className='fw-bold text-success'>{maxAvail}</span>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* UOM read-only */}
                            <td className='align-middle text-center'>{uomName || '-'}</td>

                            {/* Qty editable with maxAvail ثابت */}
                            <td className='align-middle'>
                              <PopoverCustom
                                childern={
                                  <input
                                    type='text'
                                    autoComplete='off'
                                    className='form-control text-center px-0'
                                    value={Number(line.qty || 0)}
                                    onChange={e => handleQtyChange(index, Number((e.target as HTMLInputElement).value))}
                                    inputMode='numeric'
                                    onInput={e => {
                                      const input = e.target as HTMLInputElement;
                                      input.value = input.value.replace(/[^0-9]/g, '');
                                      const v = Number(input.value || 0);
                                      if (v > maxAvail) input.value = String(maxAvail);
                                    }}
                                  />
                                }
                                Header={t('تفاصيل الكمية')}
                                Body={
                                  <div className='d-flex justify-content-between'>
                                    <span className='mt-1'>{t('الكمية المتاحة للإرجاع')}:</span>
                                    <span className='text-success fw-bold fs-5 mx-2'>{maxAvail}</span>
                                  </div>
                                }
                              />
                            </td>

                            {/* price read-only */}
                            <td className='align-middle text-center'>{formatNumber(Number(line.price || 0))}</td>
                            <td className='align-middle text-center'>{formatNumber(Number(line.vatAmount || 0))}</td>

                            {/* total read-only */}
                            <td className='align-middle text-center text-theme fw-bold'>
                              {formatNumber(Number(line.total || 0) + Number(line.vatAmount))}
                            </td>

                            {/* description editable */}
                            {/* <td className='align-middle'>
                              <input
                                type='text'
                                autoComplete='off'
                                className='form-control'
                                value={line.description || ''}
                                onChange={e => handleDescChange(index, (e.target as HTMLInputElement).value)}
                              />
                            </td> */}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {formik.errors.transactionsLinesList?.length ? <div className='text-danger p-2'> * مطلوب </div> : null}
            </div>
          )}
        </div>
      </Loading>
    </>
  );
};

export default UpdateReturnLines;
